import * as SheetPrimitive from "@radix-ui/react-dialog";
import { type VariantProps, cva } from "class-variance-authority";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "../lib/utils";
import { IconButton } from "./button";
import { Tooltip, TooltipContent, TooltipTrigger } from "./tooltip";

const Sheet = SheetPrimitive.Root;

const SheetTrigger = SheetPrimitive.Trigger;
const SheetClose = SheetPrimitive.Close;
const SheetPortal = SheetPrimitive.Portal;
type SheetOverlayProps = React.ComponentPropsWithoutRef<
  typeof SheetPrimitive.Overlay
> & { ref?: React.Ref<HTMLDivElement> };
const SheetOverlay = ({ className, ref, ...props }: SheetOverlayProps) => (
  <SheetPrimitive.Overlay
    className={cn(
      "fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className,
    )}
    {...props}
    ref={ref}
  />
);
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName;

const sheetVariants = cva(
  "fixed pt-6 z-50 gap-4 bg-background shadow-lg transition ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-500 data-[state=open]:animate-in data-[state=closed]:animate-out",
  {
    variants: {
      side: {
        top: "inset-x-0 top-0 border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top",
        bottom:
          "inset-x-0 bottom-0 border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
        left: "inset-y-0 left-0 h-full border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left ",
        right:
          "inset-y-0 right-0 h-full border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right ",
      },
    },
    defaultVariants: {
      side: "right",
    },
  },
);

interface SheetContentProps
  extends React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content>,
    VariantProps<typeof sheetVariants> {
  header?: React.ReactNode;
  enableFullscreen?: boolean;
  enableCopyUrl?: boolean;
  onToggleFullscreen?: () => void;
  isFullscreen?: boolean;
  onCopyUrl?: () => void;
}

type SheetContentPropsWithRef = SheetContentProps & {
  ref?: React.Ref<HTMLDivElement>;
};
const SheetContent = ({
  side = "right",
  className,
  children,
  header,
  enableFullscreen = false,
  enableCopyUrl = false,
  isFullscreen = false,
  onToggleFullscreen,
  onCopyUrl,
  ref,
  ...props
}: SheetContentPropsWithRef) => {
  const transitionClasses = cn(
    "transition-all duration-300 ease-in-out data-[state=open]:duration-300",
  );
  const { t } = useTranslation();
  const [isCopied, setIsCopied] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const handleCopyUrl = () => {
    onCopyUrl?.();
    setIsCopied(true);
    setTooltipOpen(true);
    setTimeout(() => {
      setIsCopied(false);
      setTooltipOpen(false);
    }, 3000);
  };

  return (
    <SheetPortal>
      <SheetOverlay />
      <SheetPrimitive.Content
        ref={ref}
        className={cn(
          sheetVariants({ side }),
          className,
          "overflow-y-auto pink-scroll px-4",
          transitionClasses,
        )}
        {...props}
      >
        <div className="flex items-start space-x-2">
          <div className="flex-1">{header}</div>
          <div className="items-center space-x-2 mt-1">
            {enableCopyUrl && onCopyUrl && (
              <Tooltip open={tooltipOpen} onOpenChange={setTooltipOpen}>
                <TooltipTrigger asChild>
                  <IconButton
                    variant="secondary"
                    className={isCopied ? "opacity-60" : ""}
                    onClick={handleCopyUrl}
                    icon={isCopied ? "check" : "link"}
                    size="xs"
                  />
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  {isCopied ? t("copied") : t("copy_link")}
                </TooltipContent>
              </Tooltip>
            )}
            {enableFullscreen && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <IconButton
                    variant="secondary"
                    onClick={onToggleFullscreen}
                    icon={isFullscreen ? "minimize-2" : "maximize-2"}
                    size="xs"
                  />
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  {isFullscreen ? t("collapse_frame") : t("expand_frame")}
                </TooltipContent>
              </Tooltip>
            )}
            <Tooltip>
              <TooltipTrigger asChild>
                <SheetPrimitive.Close asChild>
                  <IconButton icon="x" size="xs" />
                </SheetPrimitive.Close>
              </TooltipTrigger>
              <TooltipContent side="bottom">{t("close_frame")}</TooltipContent>
            </Tooltip>
          </div>
        </div>
        <div>{children}</div>
      </SheetPrimitive.Content>
    </SheetPortal>
  );
};
SheetContent.displayName = SheetPrimitive.Content.displayName;

const SheetHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-2 text-center sm:text-left",
      className,
    )}
    {...props}
  />
);
SheetHeader.displayName = "SheetHeader";
const SheetFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end w-full border-t border-gray-200",
      className,
    )}
    {...props}
  />
);
SheetFooter.displayName = "SheetFooter";
type SheetTitleProps = React.ComponentPropsWithoutRef<
  typeof SheetPrimitive.Title
> & { ref?: React.Ref<HTMLSpanElement> };
const SheetTitle = ({ className, ref, ...props }: SheetTitleProps) => (
  <span ref={ref} className={cn("font-semibold", className)} {...props} />
);
SheetTitle.displayName = SheetPrimitive.Title.displayName;
type SheetDescriptionProps = React.ComponentPropsWithoutRef<
  typeof SheetPrimitive.Description
> & { ref?: React.Ref<HTMLParagraphElement> };
const SheetDescription = ({
  className,
  ref,
  ...props
}: SheetDescriptionProps) => (
  <SheetPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
);
SheetDescription.displayName = SheetPrimitive.Description.displayName;
export {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetOverlay,
  SheetPortal,
  SheetTitle,
  SheetTrigger,
};
